import { PeriodOption } from '@/config/model';
import {
    Interval,
    addWeeks,
    eachDayOfInterval,
    eachMonthOfInterval,
    eachQuarterOfInterval,
    eachWeekOfInterval,
    eachYearOfInterval,
    format,
    parse,
    startOfISOWeek,
} from 'date-fns';

interface DateFormatter {
    format: string;
    intervalGeneratorFn: (interval: Interval) => Date[];
    parserFn: (date: string) => Date;
}

const getPeriodDateFormatter = (period: PeriodOption): DateFormatter => {
    switch (period) {
        case 'yearly':
            return {
                format: 'yyyy',
                intervalGeneratorFn: eachYearOfInterval,
                parserFn: (date) => parse(date, 'yyyy', new Date()),
            };
        case 'quarterly':
            return {
                format: 'QQQ-yyyy',
                intervalGeneratorFn: eachQuarterOfInterval,
                parserFn: (date) => parse(date, 'QQQ-yyyy', new Date()),
            };
        case 'monthly':
            return {
                format: 'MM-yyyy',
                intervalGeneratorFn: eachMonthOfInterval,
                parserFn: (date) => parse(date, 'MM-yyyy', new Date()),
            };
        case 'daily':
            return {
                format: 'dd-MM-yyyy',
                intervalGeneratorFn: eachDayOfInterval,
                parserFn: (date) => parse(date, 'dd-MM-yyyy', new Date()),
            };
        default:
            return {
                format: 'II-RRRR',
                intervalGeneratorFn: (interval) =>
                    eachWeekOfInterval(interval, { weekStartsOn: 1 }),
                parserFn: (date: string) => {
                    const [week, year] = date.split('-').map(Number);

                    if (
                        !week ||
                        week < 1 ||
                        week > 53 ||
                        !year ||
                        isNaN(week) ||
                        isNaN(year)
                    ) {
                        throw new Error(`Invalid date format: ${date}`);
                    }

                    const jan4 = new Date(year, 0, 4);
                    const firstMonday = startOfISOWeek(jan4);
                    return addWeeks(firstMonday, week - 1);
                },
            };
    }
};

export const fillDateGaps = (
    dates: string[],
    period: PeriodOption,
): string[] => {
    if (dates.length === 0) return dates;

    const {
        format: dateFormat,
        intervalGeneratorFn,
        parserFn,
    } = getPeriodDateFormatter(period);

    const validDates = dates
        .map((date) => {
            try {
                const parsed = parserFn(date);
                return isNaN(parsed.getTime()) ? null : parsed.getTime();
            } catch {
                return null;
            }
        })
        .filter((time): time is number => time !== null);

    if (validDates.length === 0) return dates;

    const minDate = new Date(Math.min(...validDates));
    const maxDate = new Date(Math.max(...validDates));

    const allDates = intervalGeneratorFn({ start: minDate, end: maxDate });
    const formattedDates = allDates.map((date) => format(date, dateFormat));

    if (period === 'weekly') {
        const originalDateSet = new Set(dates);
        return formattedDates.filter(
            (date) => !date.startsWith('53-') || originalDateSet.has(date),
        );
    }

    return formattedDates;
};
